import { useCallback } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

export interface SwUpdateState {
  /** A NEW worker is installed and waiting. False on first install. */
  needRefresh: boolean;
  /** Promote the waiting worker and reload. */
  applyUpdate: () => void;
  /** Dismiss until the next update is detected. */
  dismiss: () => void;
}

/**
 * A mockable seam over the plugin's virtual registration module, so every
 * consumer — and every test that renders the root route — mocks one ordinary
 * module path instead of a build-time virtual specifier.
 *
 * `offlineReady` is deliberately not surfaced: a transient "ready to work
 * offline" note claims a durability the browser can revoke at any time, so this
 * portal states durability where it can be qualified rather than in a toast.
 */
export function useSwUpdate(): SwUpdateState {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError: (error: unknown) => {
      // A portal that cannot register still works online; swallowing this would
      // hide a broken offline promise from the only people who could notice it.
      console.error("service worker registration failed", error);
    },
  });

  const applyUpdate = useCallback(() => {
    // No argument, deliberately: the client's `reloadPage` parameter is unused,
    // so the call only posts SKIP_WAITING to the worker that is already
    // waiting. The reload comes from the `controlling` listener the client
    // armed when that worker reached the waiting state.
    void updateServiceWorker();
  }, [updateServiceWorker]);

  const dismiss = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  return { needRefresh, applyUpdate, dismiss };
}
